import Dexie, { type Table } from 'dexie';

/** Uma configuração do aparelho: o token da sessão, a obra aberta. */
export interface AppSettingsRow {
  key: string;
  value: unknown;
  updatedAt: string;
}

/**
 * Uma leitura guardada para quando faltar sinal (Fase 43, RN-597).
 *
 * `text` é o corpo cru, como o servidor o escreveu: quem o lê de volta passa pelo mesmo
 * `valueObjectReviver` da resposta da rede, e `Money` e `Hours` voltam com método.
 * `namespace` é o hash do token — cada sessão só alcança o que ela mesma leu.
 */
export interface ApiResponseRow {
  key: string;
  namespace: string;
  text: string;
  savedAt: string;
}

/**
 * Uma operação da Conferência feita sem sinal, à espera de subir (Fase 43, etapa 2, RN-603).
 *
 * A forma é a do `OutboxEntry` de `@/app/outbox-types`; as fotos vão dentro, como `File`, que o
 * IndexedDB guarda como guarda qualquer valor.
 */
export interface OutboxRow {
  id: string;
  userId: string;
  /** A ordem em que foi feita: a hora não basta, duas operações cabem no mesmo milissegundo. */
  seq: number;
  createdAt: string;
  operation: unknown;
  state: 'pending' | 'rejected';
  error?: { code: string; message: string };
  attempts: number;
}

export const DB_NAME = 'obraplan';

/**
 * O banco do navegador, depois da Fase 32: só o que é do aparelho.
 *
 * Até a versão 34 ele guardava a obra inteira — 74 tabelas, do perfil ao apontamento por
 * peça —, uma segunda cópia de dados que a produção lia da API desde a Fase 12. A versão 35
 * fica com o `appSettings`, onde moram o token da sessão e a obra aberta, e apaga o resto.
 *
 * **Só a última é declarada, e é o que apaga.** A cada versão, o Dexie remove do banco toda
 * tabela que o esquema dela não traz (`deleteRemovedTables`), venha o navegador da 34 ou de
 * qualquer versão anterior. Listar as 73 como `null` diria o mesmo com mais linhas.
 *
 * **O número sobe** (D7), porque é a subida que apaga: o Dexie só remove tabela ao atualizar
 * a versão. Declarar a 34, ou menos, com só o `appSettings` abriria o banco como está, com
 * as 73 tabelas dentro. E o `appSettings` mantém a chave primária de sempre (`key`):
 * trocá-la exigiria recriar a tabela, e o token iria junto — todo mundo seria mandado a
 * entrar de novo.
 *
 * **Voltar a interface não derruba ninguém** (conferido em 20/10/2026, Dexie 4.4.5). Um
 * deploy anterior à Fase 32, que declara a 34, recebe `VersionError` deste banco; o Dexie
 * reabre sem número e recria vazias as tabelas que faltam. O token continua lá, e a tela
 * velha funciona como num navegador novo. Ao publicar de novo, a 35 abre o banco como ficou
 * (as tabelas recriadas sobram, vazias, e não atrapalham). `db.test.ts` guarda a ida e a
 * volta.
 */
export class ObraPlanDb extends Dexie {
  appSettings!: Table<AppSettingsRow, string>;
  apiResponses!: Table<ApiResponseRow, string>;
  outbox!: Table<OutboxRow, string>;

  constructor(name: string = DB_NAME) {
    super(name);
    // A 36 (Fase 43) acrescentou as leituras guardadas, e a 37 a fila da Conferência. Continua
    // uma só declarada: quem vem da 34 ou de antes sobe direto, e as tabelas de operação saem do
    // mesmo jeito.
    this.version(37).stores({
      appSettings: 'key',
      apiResponses: 'key, namespace, savedAt',
      outbox: 'id, userId, seq',
    });
  }
}

let instance: ObraPlanDb | undefined;

export function getDb(): ObraPlanDb {
  instance ??= new ObraPlanDb();
  return instance;
}
